// src/components/parts/PartsTracker.js
// The Parts Tracker slide: the full table (Part No, Description, Task type,
// Task, Engineer, QC, Status, Priority, Start date, Target Date, Delivery Date, Estimation,
// Actual hours, Loop, TAT), the Search Filters slide, and Excel import/export.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Paper, Typography, Button, IconButton, Tooltip, Chip, Alert, Grid,
    TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TablePagination, TableSortLabel, Dialog, DialogTitle, DialogContent,
    Divider, Skeleton, Stack, InputAdornment, Menu, LinearProgress, Collapse,
    Badge, CircularProgress,
} from '@mui/material';
import {
    Add, Refresh, Search, Clear, Edit, Timeline, Comment, Delete, FilterList,
    Download, Upload, AttachFile, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAuth } from '../../context/AuthContext';
import useReferenceData from '../../hooks/useReferenceData';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import socket from '../../services/socket';
import {
    getParts, deletePart, downloadParts, downloadPartsTemplate, importParts, getPartFiles,
} from '../../services/endpoints';
import {
    statusChipColor, priorityChipColor, fmtDate, fmtTatDays, isManagerRole, STATUS_COLORS,
} from '../../constants/domain';
import AddPart from './AddPart';
import EditPart from './EditPart';
import PartComments from './PartComments';
import PartStatusFlow from './PartStatusFlow';

const EMPTY_FILTERS = {
    search: '', task: '', task_type: '', engineer: '', priority: '',
    status: '', customer: '', date_from: '', date_to: '', co_number: '',
    overdue: '',
};

const COLUMNS = [
    { key: 'part_number',     label: 'Part No',      sortable: true  },
    { key: 'description',     label: 'Description',  sortable: false },
    { key: 'task_type',       label: 'Task type',    sortable: false },
    { key: 'task',            label: 'Task',         sortable: true  },
    { key: 'assigned_to_name',label: 'Engineer',     sortable: true, sortKey: 'engineer' },
    { key: 'qc_engineer_name',label: 'QC',           sortable: false },
    { key: 'status',          label: 'Status',        sortable: true  },
    { key: 'priority',        label: 'Priority',      sortable: true  },
    { key: 'start_date',      label: 'Start date',    sortable: true  },
    { key: 'due_date',        label: 'Target Date',   sortable: true  },
    { key: 'delivery_date',   label: 'Delivery Date', sortable: false },
    { key: 'estimated_hours', label: 'Estimation',    sortable: false, align: 'right' },
    { key: 'actual_hours',    label: 'Actual hours',  sortable: false, align: 'right' },
    { key: 'loop_count',      label: 'Loop',          sortable: false, align: 'center' },
    { key: 'tat_hours',       label: 'TAT (days)',    sortable: true, sortKey: 'tat', align: 'center' },
];

const PartsTracker = () => {
    const { user } = useAuth();
    const ref = useReferenceData();
    const manager = isManagerRole(user?.role);
    const [searchParams, setSearchParams] = useSearchParams();

    const [parts, setParts] = useState([]);
    const [error, setError] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    // Seed filters from the URL so the dashboard can deep-link into a view.
    const initialFilters = useMemo(() => {
        const seeded = { ...EMPTY_FILTERS };
        for (const key of Object.keys(EMPTY_FILTERS)) {
            const value = searchParams.get(key);
            if (value) seeded[key] = value;
        }
        return seeded;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [filters, setFilters] = useState(initialFilters);
    const [applied, setApplied] = useState(initialFilters);

    const [sortBy, setSortBy] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [selected, setSelected] = useState(null);
    const [dialog, setDialog] = useState(null);   // add | edit | flow | comments
    const [expanded, setExpanded] = useState(null);
    const [expandedFiles, setExpandedFiles] = useState([]);
    const [importing, setImporting] = useState(false);
    const [exportMenu, setExportMenu] = useState(null);

    const fetchParts = useCallback(async () => {
        try {
            const res = await getParts({ ...applied, sort_by: sortBy, sort_dir: sortDir });
            setParts(res.data || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    }, [applied, sortBy, sortDir]);

    const { lastRefresh, refreshing, refresh } = useAutoRefresh(fetchParts);

    useEffect(() => { fetchParts(); }, [fetchParts]);

    // Live updates from other users.
    useEffect(() => {
        const onChange = () => fetchParts();
        ['statusUpdate', 'partCreated', 'partUpdated', 'partDeleted', 'partsImported']
            .forEach(e => socket.on(e, onChange));
        return () => {
            ['statusUpdate', 'partCreated', 'partUpdated', 'partDeleted', 'partsImported']
                .forEach(e => socket.off(e, onChange));
        };
    }, [fetchParts]);

    const activeFilterCount = Object.values(applied).filter(Boolean).length;

    useEffect(() => {
        if (activeFilterCount > 0) setShowFilters(true);
        // The dashboard links here with ?add=1 to jump straight into Add Part.
        if (searchParams.get('add')) {
            setDialog('add');
            searchParams.delete('add');
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = () => {
        setApplied(filters);
        setPage(0);
        // Keep the URL in step so the view is shareable.
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
        setSearchParams(params, { replace: true });
    };

    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setApplied(EMPTY_FILTERS);
        setPage(0);
        setSearchParams(new URLSearchParams(), { replace: true });
    };

    const handleSort = (column) => {
        const key = column.sortKey || column.key;
        if (sortBy === key) {
            setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(key);
            setSortDir('asc');
        }
    };

    const openDialog = (kind, part) => { setSelected(part || null); setDialog(kind); };
    const closeDialog = () => { setDialog(null); setSelected(null); };

    const canEditPart = (part) =>
        manager || part.assigned_to === user?.id || part.qc_engineer === user?.id;

    const handleDelete = async (part) => {
        if (!window.confirm(`Delete part ${part.part_number}? This cannot be undone.`)) return;
        try {
            await deletePart(part.id);
            toast.success(`Part ${part.part_number} deleted`);
            fetchParts();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        }
    };

    const handleExport = async (format) => {
        setExportMenu(null);
        try {
            await downloadParts(format, applied);
            toast.success(`Exported as ${format.toUpperCase()}`);
        } catch (err) {
            toast.error(`Export failed: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleDownloadTemplate = async () => {
        setExportMenu(null);
        try {
            await downloadPartsTemplate();
            toast.success('Bulk upload template downloaded');
        } catch (err) {
            toast.error(`Download failed: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleImport = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';           // allow re-picking the same file
        if (!file) return;

        setImporting(true);
        try {
            const res = await importParts(file);
            const { created, updated, skipped, errors } = res.data;
            toast.success(`Imported: ${created} created, ${updated} updated, ${skipped} skipped`);
            if (errors?.length) {
                toast.warning(`${errors.length} row(s) failed - see console for detail`);
                console.warn('Import errors:', errors);
            }
            fetchParts();
        } catch (err) {
            toast.error(`Import failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setImporting(false);
        }
    };

    const toggleExpand = async (part) => {
        if (expanded === part.id) {
            setExpanded(null);
            setExpandedFiles([]);
            return;
        }
        setExpanded(part.id);
        setExpandedFiles([]);
        try {
            const res = await getPartFiles(part.id);
            setExpandedFiles(res.data || []);
        } catch {
            setExpandedFiles([]);
        }
    };

    const paged = parts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const fileHost = process.env.REACT_APP_SOCKET_URL || '';

    const cellValue = (part, key) => {
        switch (key) {
            case 'task_type':
                return <Chip label={part.task_type || '-'} size="small" variant="outlined" />;
            case 'status':
                return <Chip label={part.status} size="small" color={statusChipColor(part.status)} />;
            case 'priority':
                return (
                    <Chip label={part.priority} size="small" variant="outlined"
                        color={priorityChipColor(part.priority)} />
                );
            case 'start_date':
            case 'due_date':
            case 'delivery_date':
                return fmtDate(part[key]) || '-';
            case 'estimated_hours':
            case 'actual_hours':
                return part[key] != null ? Number(part[key]).toFixed(1) : '-';
            case 'tat_hours':
                return fmtTatDays(part.tat_hours) ?? '-';
            case 'loop_count':
                return part.loop_count > 0
                    ? <Chip label={part.loop_count} size="small" color="warning" />
                    : '0';
            case 'description':
                return (
                    <Tooltip title={part.description || part.part_name || ''}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {part.description || part.part_name || '-'}
                        </Typography>
                    </Tooltip>
                );
            default:
                return part[key] || '-';
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                mb: 2, flexWrap: 'wrap', gap: 1,
            }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Parts Tracker</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {parts.length} part{parts.length === 1 ? '' : 's'}
                        {activeFilterCount > 0 && ' (filtered)'}
                        {lastRefresh && ` — updated ${lastRefresh.toLocaleTimeString('en-GB')}`}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Tooltip title="Refresh">
                        <IconButton onClick={refresh} disabled={refreshing}><Refresh /></IconButton>
                    </Tooltip>
                    <Badge badgeContent={activeFilterCount} color="primary">
                        <Button variant="outlined" startIcon={<FilterList />}
                            onClick={() => setShowFilters(s => !s)}>
                            Filters
                        </Button>
                    </Badge>
                    <Button variant="outlined" startIcon={<Download />}
                        onClick={(e) => setExportMenu(e.currentTarget)}>
                        Export
                    </Button>
                    <Menu anchorEl={exportMenu} open={!!exportMenu} onClose={() => setExportMenu(null)}>
                        <MenuItem onClick={() => handleExport('excel')}>Excel (.xlsx)</MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>PDF</MenuItem>
                        <MenuItem onClick={() => handleExport('csv')}>CSV</MenuItem>
                        <MenuItem onClick={handleDownloadTemplate}>
                            Bulk upload template (.xlsx)
                        </MenuItem>
                    </Menu>
                    <Button component="label" variant="outlined" disabled={importing}
                        startIcon={importing ? <CircularProgress size={16} /> : <Upload />}>
                        {importing ? 'Importing...' : 'Import'}
                        <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} />
                    </Button>
                    <Button variant="contained" startIcon={<Add />} onClick={() => openDialog('add')}>
                        Add Part
                    </Button>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {importing && <LinearProgress sx={{ mb: 2 }} />}

            {/* Search filters */}
            <Collapse in={showFilters}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} md={3}>
                            <TextField fullWidth size="small" label="Search"
                                placeholder="Part no, description, task, customer"
                                value={filters.search}
                                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1.5}>
                            <TextField fullWidth size="small" label="Task"
                                value={filters.task}
                                onChange={(e) => setFilters(f => ({ ...f, task: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1.5}>
                            <TextField select fullWidth size="small" label="Task type"
                                value={filters.task_type}
                                onChange={(e) => setFilters(f => ({ ...f, task_type: e.target.value }))}>
                                <MenuItem value="">All</MenuItem>
                                {ref.taskTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField select fullWidth size="small" label="Engineer"
                                value={filters.engineer}
                                onChange={(e) => setFilters(f => ({ ...f, engineer: e.target.value }))}>
                                <MenuItem value="">All</MenuItem>
                                {ref.engineers.map(u => (
                                    <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={1.5}>
                            <TextField select fullWidth size="small" label="Priority"
                                value={filters.priority}
                                onChange={(e) => setFilters(f => ({ ...f, priority: e.target.value }))}>
                                <MenuItem value="">All</MenuItem>
                                {ref.priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.5}>
                            <TextField select fullWidth size="small" label="Status"
                                value={filters.status}
                                onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
                                <MenuItem value="">All</MenuItem>
                                {ref.statuses.map(s => (
                                    <MenuItem key={s} value={s}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                width: 8, height: 8, borderRadius: '50%',
                                                bgcolor: STATUS_COLORS[s] || 'grey.400',
                                            }} />
                                            {s}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <TextField fullWidth size="small" label="Customer"
                                value={filters.customer}
                                onChange={(e) => setFilters(f => ({ ...f, customer: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField fullWidth size="small" label="CO Number"
                                value={filters.co_number}
                                onChange={(e) => setFilters(f => ({ ...f, co_number: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField fullWidth size="small" type="date" label="Start from"
                                value={filters.date_from}
                                onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                                InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField fullWidth size="small" type="date" label="Target before"
                                value={filters.date_to}
                                onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                                InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField select fullWidth size="small" label="Overdue only"
                                value={filters.overdue}
                                onChange={(e) => setFilters(f => ({ ...f, overdue: e.target.value }))}>
                                <MenuItem value="">No</MenuItem>
                                <MenuItem value="true">Yes</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction="row" spacing={1}>
                                <Button size="small" variant="contained" onClick={applyFilters}>
                                    Apply filters
                                </Button>
                                <Button size="small" startIcon={<Clear />} onClick={clearFilters}
                                    disabled={activeFilterCount === 0}>
                                    Clear
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
            </Collapse>

            {/* Table */}
            <Paper>
                <TableContainer sx={{ maxHeight: 640 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 40 }} />
                                {COLUMNS.map(col => (
                                    <TableCell key={col.key} align={col.align || 'left'}
                                        sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {col.sortable ? (
                                            <TableSortLabel
                                                active={sortBy === (col.sortKey || col.key)}
                                                direction={sortBy === (col.sortKey || col.key) ? sortDir : 'asc'}
                                                onClick={() => handleSort(col)}>
                                                {col.label}
                                            </TableSortLabel>
                                        ) : col.label}
                                    </TableCell>
                                ))}
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {refreshing && parts.length === 0 && [...Array(6)].map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell colSpan={COLUMNS.length + 2}>
                                        <Skeleton height={28} />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!refreshing && parts.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={COLUMNS.length + 2} align="center" sx={{ py: 5 }}>
                                        <Typography color="text.secondary">
                                            {activeFilterCount > 0
                                                ? 'No parts match these filters.'
                                                : 'No parts yet. Use "Add Part" to create one.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {paged.map(part => (
                                <React.Fragment key={part.id}>
                                    <TableRow hover
                                        sx={{
                                            borderLeft: part.is_overdue ? '3px solid' : 'none',
                                            borderLeftColor: 'error.main',
                                        }}>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => toggleExpand(part)}>
                                                {expanded === part.id ? <ExpandLess /> : <ExpandMore />}
                                            </IconButton>
                                        </TableCell>
                                        {COLUMNS.map(col => (
                                            <TableCell key={col.key} align={col.align || 'left'}
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    fontWeight: col.key === 'part_number' ? 600 : 400,
                                                    color: (col.key === 'due_date' && part.is_overdue)
                                                        ? 'error.main' : undefined,
                                                }}>
                                                {cellValue(part, col.key)}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <Tooltip title="Status flow">
                                                <IconButton size="small"
                                                    disabled={!canEditPart(part)}
                                                    onClick={() => openDialog('flow', part)}>
                                                    <Timeline fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Comments">
                                                <IconButton size="small"
                                                    onClick={() => openDialog('comments', part)}>
                                                    <Badge badgeContent={Number(part.comment_count) || 0}
                                                        color="primary">
                                                        <Comment fontSize="small" />
                                                    </Badge>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={canEditPart(part) ? 'Edit' : 'Not assigned to you'}>
                                                <IconButton size="small"
                                                    disabled={!canEditPart(part)}
                                                    onClick={() => openDialog('edit', part)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {manager && (
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" color="error"
                                                        onClick={() => handleDelete(part)}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>

                                    {/* Expanded detail */}
                                    <TableRow>
                                        <TableCell colSpan={COLUMNS.length + 2}
                                            sx={{ py: 0, borderBottom: expanded === part.id ? undefined : 'none' }}>
                                            <Collapse in={expanded === part.id} unmountOnExit>
                                                <Box sx={{ py: 2 }}>
                                                    <Grid container spacing={2}>
                                                        {[
                                                            ['Revision', part.revision],
                                                            ['CO Number', part.co_number],
                                                            ['Customer', part.customer],
                                                            ['Created by', part.created_by_name],
                                                            ['Progress', `${part.completion_percentage ?? 0}%`],
                                                            ['Completed', fmtDate(part.completed_at)],
                                                        ].map(([label, value]) => (
                                                            <Grid item xs={6} sm={4} md={2} key={label}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {label}
                                                                </Typography>
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    {value || '-'}
                                                                </Typography>
                                                            </Grid>
                                                        ))}
                                                        {part.hold_reason && (
                                                            <Grid item xs={12}>
                                                                <Alert severity="warning" sx={{ py: 0 }}>
                                                                    On hold: {part.hold_reason}
                                                                </Alert>
                                                            </Grid>
                                                        )}
                                                        <Grid item xs={12}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Attachments
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                                                {expandedFiles.length === 0 ? (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        None
                                                                    </Typography>
                                                                ) : expandedFiles.map(f => (
                                                                    <Chip key={f.id} size="small" clickable
                                                                        icon={<AttachFile />}
                                                                        label={f.file_name}
                                                                        component="a"
                                                                        href={`${fileHost}${f.file_path}`}
                                                                        target="_blank" rel="noreferrer" />
                                                                ))}
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={parts.length}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Paper>

            {/* Dialogs */}
            <Dialog open={dialog === 'add'} onClose={closeDialog} maxWidth="md" fullWidth>
                <DialogTitle>Add Part</DialogTitle>
                <Divider />
                <DialogContent>
                    <AddPart
                        onSuccess={() => { closeDialog(); fetchParts(); }}
                        onCancel={closeDialog}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={dialog === 'edit'} onClose={closeDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <span>Edit Part</span>
                        <Chip label={selected?.part_number} size="small" color="primary" />
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <EditPart
                        part={selected}
                        onSuccess={() => { closeDialog(); fetchParts(); }}
                        onCancel={closeDialog}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={dialog === 'comments'}
                onClose={() => { closeDialog(); fetchParts(); }} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <span>Comments</span>
                        <Chip label={selected?.part_number} size="small" variant="outlined" />
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {selected && (
                        <PartComments partId={selected.id}
                            onClose={() => { closeDialog(); fetchParts(); }} />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={dialog === 'flow'} onClose={closeDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <span>Status flow</span>
                        <Chip label={selected?.part_number} size="small" color="primary" />
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {selected && (
                        <PartStatusFlow
                            part={selected}
                            onStatusUpdate={fetchParts}
                            onTaskUpdate={() => window.dispatchEvent(new CustomEvent('taskUpdate'))}
                            onClose={closeDialog}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default PartsTracker;
